import React, { useState, useEffect } from 'react';
import { User, Crown, Loader2, Shield } from 'lucide-react';
import PlayingCard from './PlayingCard';

const PokerTable = ({ gameState, onAction, actionInProgress }) => {
  // ALL HOOKS AT TOP - BEFORE ANY RETURNS
  const [raiseAmount, setRaiseAmount] = useState(0);
  const [showRaiseControls, setShowRaiseControls] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  // Initialize slider - Hook before any returns
  useEffect(() => {
    if (showRaiseControls && raiseAmount === 0 && gameState) {
      const humanPlayer = gameState.players?.find(p => p.is_human);
      const current_bet = gameState.current_bet || 0;
      const minRaise = current_bet > 0 ? current_bet * 2 : 20;
      setRaiseAmount(minRaise);
      setSliderValue(minRaise);
    }
  }, [showRaiseControls, raiseAmount, gameState]);

  // NOW safe to do early return AFTER all hooks
  if (!gameState) return null;

  const { players, pot, current_bet, community_cards, dealer_position, current_player_index, street } = gameState;
  const humanPlayer = players.find(p => p.is_human);
  
  const isHumanTurn = players[current_player_index]?.is_human;
  const callAmount = current_bet - (humanPlayer?.current_bet || 0);
  const minRaise = current_bet > 0 ? current_bet * 2 : 20;
  const maxRaise = humanPlayer?.chips || 1000;

  const handleAction = (action, amount = 0) => {
    onAction(action, amount);
    setShowRaiseControls(false);
    setRaiseAmount(0);
    setSliderValue(0);
  };

  const handleRaise = () => {
    handleAction('raise', raiseAmount || minRaise);
  };

  const handleQuickBet = (multiplier) => {
    let amount;
    if (multiplier === 'pot') {
      amount = pot;
    } else if (multiplier === 'allin') {
      amount = humanPlayer?.chips || 0;
    } else {
      amount = current_bet * multiplier;
    }
    amount = Math.min(amount, maxRaise);
    setRaiseAmount(amount);
    setSliderValue(amount);
  };

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);
    setRaiseAmount(value);
  };

  // Position players in oval around table - responsive
  const getPlayerPosition = (index, total) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const radiusX = 43; // Horizontal radius
    const radiusY = 36; // Vertical radius
    const x = 50 + radiusX * Math.cos(angle);
    const y = 50 + radiusY * Math.sin(angle);
    return { left: `${x}%`, top: `${y}%` };
  };

  const formatChips = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount;
  };

  const getStreetDisplay = () => {
    const streets = {
      'preflop': 'Pre-Flop',
      'flop': 'Flop',
      'turn': 'Turn',
      'river': 'River',
      'showdown': 'Showdown'
    };
    return streets[street] || street;
  };

  return (
    <div className="relative w-full max-w-[900px] mx-auto mt-4">
      
      {/* ACTION BUTTONS AT TOP */}
      {isHumanTurn && gameState && street !== 'showdown' && (
        <div className="mb-2">
          {!showRaiseControls ? (
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => handleAction('fold')} disabled={actionInProgress} data-testid="action-fold-btn" className="px-4 py-2 bg-gradient-to-b from-slate-700 to-slate-900 hover:from-slate-600 text-white rounded-lg font-bold text-sm border border-slate-600">
                FOLD
              </button>
              {callAmount === 0 && (
                <button onClick={() => handleAction('check')} disabled={actionInProgress} data-testid="action-check-btn" className="px-4 py-2 bg-gradient-to-b from-blue-600 to-blue-900 hover:from-blue-500 text-white rounded-lg font-bold text-sm border border-blue-400">
                  CHECK
                </button>
              )}
              {callAmount > 0 && (
                <button onClick={() => handleAction('call')} disabled={actionInProgress} data-testid="action-call-btn" className="px-4 py-2 bg-gradient-to-b from-blue-600 to-blue-900 hover:from-blue-500 text-white rounded-lg font-bold text-sm border border-blue-400">
                  CALL {formatChips(callAmount)}
                </button>
              )}
              <button onClick={() => setShowRaiseControls(true)} disabled={actionInProgress} data-testid="action-raise-btn" className="px-5 py-2 bg-gradient-to-b from-blue-600 to-blue-900 hover:from-blue-500 text-white rounded-lg font-bold text-sm border border-blue-400">
                RAISE
              </button>
            </div>
          ) : (
            <div className="bg-slate-900 border border-blue-500/40 rounded-lg p-2 space-y-1">
              <div className="flex gap-1 justify-center">
                <button onClick={() => handleQuickBet(2)} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-blue-300 rounded text-xs font-semibold">2x</button>
                <button onClick={() => handleQuickBet(3)} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-blue-300 rounded text-xs font-semibold">3x</button>
                <button onClick={() => handleQuickBet('pot')} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-blue-300 rounded text-xs font-semibold">POT</button>
                <button onClick={() => handleQuickBet('allin')} className="px-2 py-1 bg-red-600 hover:bg-red-500 text-white rounded text-xs font-bold">ALL IN</button>
              </div>
              <div className="px-1">
                <div className="flex justify-between mb-0.5 text-xs">
                  <span className="text-blue-300">Amt:</span>
                  <span className="text-white font-bold">${raiseAmount.toLocaleString()}</span>
                </div>
                <input type="range" min={minRaise} max={maxRaise} value={sliderValue} onChange={handleSliderChange} className="w-full h-1 bg-slate-700 rounded slider-wsop" />
              </div>
              <div className="flex gap-1 justify-center">
                <button onClick={() => setShowRaiseControls(false)} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-semibold">CANCEL</button>
                <button onClick={handleRaise} disabled={actionInProgress || raiseAmount < minRaise} className="px-4 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-bold disabled:opacity-50">
                  {actionInProgress ? <Loader2 className="w-3 h-3 animate-spin" /> : `RAISE ${formatChips(raiseAmount)}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* YOUR CARDS - ONLY ONCE, at top - Synced with HUD */}
      {humanPlayer?.hole_cards && humanPlayer.hole_cards.length > 0 && (
        <div className="mb-2 flex justify-center">
          <div className={`
            relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 
            rounded-lg p-1.5 shadow-lg transition-all duration-300
            ${isHumanTurn 
              ? 'border-2 border-[#C9A227]/60 shadow-[0_0_25px_rgba(201,162,39,0.4)] animate-hud-pulse' 
              : 'border border-amber-500/40'
            }
          `}>
            <div className="flex gap-1.5">
              {humanPlayer.hole_cards.map((card, idx) => (
                <div key={idx}>
                  <PlayingCard card={card} size="sm" />
                </div>
              ))}
            </div>
            <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-emerald-700 border border-emerald-400 rounded-full px-2 py-0.5 shadow-md">
              <span className="text-white font-bold text-xs">{formatChips(humanPlayer.chips)}</span>
            </div>
          </div>
        </div>
      )}

      {/* POKER TABLE BELOW - Auto-scaled to fit */}
      <div className="relative w-full max-w-[850px] mx-auto" style={{ aspectRatio: '16/11' }}>
        <div className="absolute inset-0 rounded-[2rem] overflow-visible shadow-2xl">
          {/* Outer rail */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1512] via-[#2d1f1a] to-[#1a1512] rounded-[2rem] p-2">
            {/* Table surface */}
            <div className="relative w-full h-full rounded-[40%] bg-gradient-radial from-[#0a6b3d] via-[#08532f] to-[#063d24] shadow-inner">
              {/* Inner rail accent */}
              <div className="absolute inset-2 rounded-[40%] border-2 border-amber-600/20"></div>
              
              {/* Subtle table pattern */}
              <div className="absolute inset-0 rounded-[40%] opacity-5" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}></div>

              {/* Center area - Pot and community cards */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
                {/* Community Cards */}
                {community_cards.length > 0 && (
                  <div className="mb-4">
                    <div className="flex gap-2 justify-center">
                      {community_cards.map((card, idx) => (
                        <div 
                          key={idx}
                          className="animate-[slideIn_0.3s_ease-out]"
                          style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                          <PlayingCard card={card} size="md" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pot Display - CLEAN & SIMPLE */}
                <div className="relative mt-4">
                  <div className="relative bg-gradient-to-b from-[#0a0a0a] to-[#050505] border-2 border-amber-500/80 rounded-2xl px-8 py-4 shadow-2xl">
                    <div className="flex flex-col items-center">
                      <div className="text-amber-400 font-bold text-3xl tracking-wide mb-1">
                        ${pot.toLocaleString()}
                      </div>
                      <div className="text-amber-500/60 text-xs uppercase tracking-widest font-semibold">POT</div>
                    </div>
                  </div>
                </div>

                {/* Action Prompt - Center */}
                {isHumanTurn && callAmount > 0 && (
                  <div className="mt-4 bg-gradient-to-r from-blue-600 to-blue-700 border-2 border-blue-400 rounded-xl px-6 py-2 shadow-lg animate-pulse">
                    <div className="text-white font-bold text-lg text-center uppercase tracking-wide">
                      {callAmount === humanPlayer?.stack ? 'ALL IN' : `RAISE OR CALL ${formatChips(callAmount)}`}
                    </div>
                  </div>
                )}
              </div>

              {/* Players positioned around oval table */}
              {players.map((player, idx) => {
                const position = getPlayerPosition(idx, players.length);
                const isDealer = idx === dealer_position;
                const isActive = idx === current_player_index;
                const isHuman = player.is_human;

                return (
                  <div
                    key={player.player_id}
                    style={position}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                  >
                    <div className={`relative transition-all duration-300 ${
                      isActive ? 'scale-110' : 'scale-100'
                    }`}>
                      {/* Player Avatar Card - WSOP Style */}
                      <div className="relative">
                        {/* Avatar Circle - Synced with HUD */}
                        <div className={`
                          relative w-20 h-20 rounded-full border-4 
                          bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden
                          transition-all duration-300
                          ${isActive && isHuman
                            ? 'border-[#C9A227] shadow-[0_0_25px_rgba(201,162,39,0.6)] animate-hud-pulse'
                            : isActive 
                            ? 'border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.6)]'
                            : 'border-blue-900'
                          }
                        `}>
                          {/* Active pulse - Synced color for human player */}
                          {isActive && (
                            <div className={`absolute inset-0 border-4 rounded-full animate-ping opacity-75 ${
                              isHuman ? 'border-[#C9A227]' : 'border-amber-400'
                            }`}></div>
                          )}
                          
                          {/* Player icon/avatar */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            {isHuman ? (
                              <Crown className="w-8 h-8 text-amber-400" />
                            ) : (
                              <User className="w-8 h-8 text-slate-400" />
                            )}
                          </div>

                          {/* Dealer button */}
                          {isDealer && (
                            <div className="absolute -top-2 -right-2 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center z-20">
                              <span className="text-black font-bold text-xs">D</span>
                            </div>
                          )}
                        </div>

                        {/* Player Info Card Below Avatar */}
                        <div className={`mt-2 bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-sm rounded-lg border-2 ${
                          isActive ? 'border-amber-500/60' : 'border-blue-900/40'
                        } px-3 py-1.5 min-w-[100px] shadow-lg`}>
                          {/* Name with shield */}
                          <div className="flex items-center gap-1 mb-1">
                            <Shield className={`w-3 h-3 ${
                              isHuman ? 'text-amber-400' : 'text-blue-500'
                            }`} />
                            <span className={`font-semibold text-xs truncate max-w-[70px] ${
                              isHuman ? 'text-amber-400' : 'text-white'
                            }`}>
                              {player.name}
                            </span>
                          </div>
                          
                          {/* Chip count - No icon */}
                          <div className="flex items-center gap-1">
                            <span className="text-emerald-400 font-bold text-sm">
                              ${formatChips(player.chips)}
                            </span>
                          </div>
                        </div>

                        {/* Current Bet Display - Clean */}
                        {player.current_bet > 0 && (
                          <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 z-30">
                            <div className="bg-black/90 border-2 border-amber-400 rounded-lg px-4 py-1.5 shadow-lg">
                              <span className="text-amber-400 font-bold text-sm whitespace-nowrap">
                                ${formatChips(player.current_bet)}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Player cards - small view */}
                        {player.hole_cards && player.hole_cards.length > 0 && !isHuman && (
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                            {player.hole_cards.map((card, cardIdx) => (
                              <PlayingCard
                                key={cardIdx}
                                card={card}
                                size="xs"
                                faceDown={!player.folded}
                              />
                            ))}
                          </div>
                        )}

                        {/* Folded overlay */}
                        {player.folded && (
                          <div className="absolute inset-0 bg-black/80 rounded-full flex items-center justify-center">
                            <span className="text-red-500 font-bold text-xs">FOLD</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PokerTable;